import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import notify from '../../../core/components/notify/notify.component';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import type { EditableFloorPlan } from '../models/editor-feature';
import {
  exportFloorPlan, publishFloorPlan, publishFloorPlanDraft, saveFloorPlanDraft, saveFloorPlanLayers, type FloorPlanDraft,
} from '../services/floorplan-editor.service';
import { serializeFloorPlan, type FloorPlanLayersPayload } from '../utils/serialize-floorplan';
import { validateFloorPlan, type ValidationIssue } from '../utils/validate-floorplan';
import { areaMessageSuffixOf, withoutUndrawableShapes } from '../utils/drawable-geometry';
import { selectionKeyOf } from '../utils/selection';

export interface SaveState {
  readonly saving: boolean;
  readonly issues: ValidationIssue[];
  /** Puts the changes live now: straight to the plan, or through the draft when there is one. */
  readonly save: () => Promise<boolean>;
  /** Keeps the changes as the plan's draft. Nothing goes live. */
  readonly saveDraft: () => Promise<boolean>;
  /** Saves the changes as the draft and schedules it to go live at `publishAt` (ISO, UTC). */
  readonly schedule: (publishAt: string) => Promise<boolean>;
  readonly exportPlan: () => Promise<void>;
}

interface Props {
  readonly floorPlan: EditableFloorPlan | undefined;
  /** Whether the document differs from what was last saved — live, or as the draft. */
  readonly dirty: boolean;
  /** The plan's draft, when it has one. */
  readonly draft: FloorPlanDraft | null | undefined;
  /** After the plan went live: the document is saved and the plan has to be read again. */
  readonly onSaved: () => void;
  /** After the draft changed: the document is saved as the draft. */
  readonly onDraftSaved: (draft: FloorPlanDraft | null) => void;
}

/**
 * Saving, drafting, scheduling and exporting — all explicit actions.
 *
 * Neither asks the admin anything: the confirmation an export needs when there are unsaved
 * changes belongs to the component that can render a dialog, not to a hook.
 *
 * There is no autosave: a floorplan is shared by every employee who books a desk from it,
 * so a half-finished rearrangement should never reach them because someone stopped to think
 * mid-drag. A draft is the way to stop halfway: it is kept, and nobody sees it until it is
 * published, now or at a time chosen for it.
 */
export const useSaveFloorPlan = ({
  floorPlan, dirty, draft, onSaved, onDraftSaved,
}: Props): SaveState => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);

  /**
   * The submission for the document, checked. Undefined, with the issues shown, when it would
   * be refused anyway.
   */
  const prepare = useCallback((): FloorPlanLayersPayload | undefined => {
    if (!floorPlan) {
      return undefined;
    }

    // Outlines that draw nothing are dropped first: they cannot be seen, clicked or fixed, and
    // the server refuses them, so without this one of them was enough to make a plan unsavable
    // for good. Each is still reported — as a note, not an error — so nothing leaves silently.
    const { floorPlan: toSave, removed } = withoutUndrawableShapes(floorPlan);
    const removalNotes: ValidationIssue[] = removed.map((feature) => ({
      severity: 'warning',
      key: selectionKeyOf(feature),
      messageKey: `floorplan-editor-validation-empty-shape-removed-${areaMessageSuffixOf(feature)}`,
    }));

    // Checked here as well as on the server so the admin gets the offending features rather
    // than a rejected request. The server remains the actual boundary.
    const validation = validateFloorPlan(toSave);
    setIssues([...removalNotes, ...validation.issues]);

    if (validation.blocking) {
      notify(t('floorplan-editor-save-validation-failed'));
      return undefined;
    }

    // The full scope: the editor now shows and edits the border, the rooms and the floor's
    // walls, so it has to speak for them. Saving under the narrow scope while they are
    // editable is the one combination that loses work silently — an admin moves a wall, saves,
    // and the wall is back where it was with no error anywhere.
    return serializeFloorPlan(toSave, 'full');
  }, [floorPlan, t]);

  const reportRefusal = useCallback((details: Record<string, string> | undefined): void => {
    const detail = details ? Object.values(details).join(' ') : undefined;
    notify(detail ?? t('floorplan-editor-save-failed'));
  }, [t]);

  /** The document as the plan's draft. Returns the saved draft, or undefined when refused. */
  const writeDraft = useCallback(async (): Promise<FloorPlanDraft | undefined> => {
    const payload = prepare();

    if (!payload || !floorPlan) {
      return undefined;
    }

    const result = await saveFloorPlanDraft(floorPlan.floorPlanId, payload, draft?.lastUpdatedAt, keycloak);

    if (!result.saved || !result.draft) {
      if (result.conflict) {
        notify(t('floorplan-editor-draft-conflict'));
      } else {
        reportRefusal(result.details);
      }

      return undefined;
    }

    return result.draft;
  }, [prepare, floorPlan, draft, keycloak, t, reportRefusal]);

  const saveDraft = useCallback(async (): Promise<boolean> => {
    setSaving(true);

    try {
      const saved = await writeDraft();

      if (!saved) {
        return false;
      }

      notify(t('floorplan-editor-draft-saved'), true);
      onDraftSaved(saved);
      return true;
    } finally {
      setSaving(false);
    }
  }, [writeDraft, onDraftSaved, t]);

  /** Publishes a draft that is already saved: now, or at `publishAt`. */
  const publishDraft = useCallback(async (publishAt?: string): Promise<boolean> => {
    if (!floorPlan) {
      return false;
    }

    const result = await publishFloorPlanDraft(floorPlan.floorPlanId, publishAt, keycloak);

    switch (result.status) {
      case 'published':
        notify(t('floorplan-editor-saved'), true);
        onSaved();
        return true;
      case 'scheduled':
        notify(t('floorplan-editor-draft-scheduled', { when: new Date(result.draft.publishAt ?? publishAt ?? '').toLocaleString() }), true);
        onDraftSaved(result.draft);
        return true;
      case 'liveConflict':
        notify(t('floorplan-editor-draft-live-conflict'));
        return false;
      case 'invalidSchedule':
        notify(t('floorplan-editor-draft-invalid-schedule'));
        return false;
      case 'invalid':
        reportRefusal(result.details);
        return false;
      default:
        notify(t('floorplan-editor-save-failed'));
        return false;
    }
  }, [floorPlan, keycloak, onSaved, onDraftSaved, reportRefusal, t]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!floorPlan) {
      return false;
    }

    setSaving(true);

    try {
      // With a draft, publishing goes through it: the server checks the live plan has not
      // changed since the draft was started, which a direct save from here could not.
      if (draft) {
        if (dirty && !(await writeDraft())) {
          return false;
        }

        return await publishDraft();
      }

      // Never published, and nothing changed: published as it stands.
      const firstPublish = floorPlan.publishedAt === null;
      if (firstPublish && !dirty) {
        if (!(await publishFloorPlan(floorPlan.floorPlanId, keycloak))) {
          notify(t('floorplan-editor-save-failed'));
          return false;
        }

        notify(t('floorplan-editor-published-first'), true);
        onSaved();
        return true;
      }

      const payload = prepare();

      if (!payload) {
        return false;
      }

      const result = await saveFloorPlanLayers(floorPlan.floorPlanId, payload, keycloak);

      if (!result.saved) {
        if (result.conflict) {
          // Nothing was written, and the edits are still on screen. Saying so plainly matters:
          // the admin has to reopen the plan and redo the change, and a generic failure would
          // leave them pressing Save again against the same stale copy.
          notify(t('floorplan-editor-save-conflict'));
          return false;
        }

        reportRefusal(result.details);
        return false;
      }

      // Saved live, and the first time it goes live for the app too.
      if (firstPublish && !(await publishFloorPlan(floorPlan.floorPlanId, keycloak))) {
        notify(t('floorplan-editor-saved-not-published'));
        onSaved();
        return false;
      }

      notify(t(firstPublish ? 'floorplan-editor-published-first' : 'floorplan-editor-saved'), true);
      onSaved();
      return true;
    } finally {
      setSaving(false);
    }
  }, [floorPlan, draft, dirty, writeDraft, publishDraft, prepare, keycloak, onSaved, reportRefusal, t]);

  const schedule = useCallback(async (publishAt: string): Promise<boolean> => {
    setSaving(true);

    try {
      // The schedule belongs to the draft, so there has to be one holding these changes.
      if ((dirty || !draft) && !(await writeDraft())) {
        return false;
      }

      return await publishDraft(publishAt);
    } finally {
      setSaving(false);
    }
  }, [dirty, draft, writeDraft, publishDraft]);

  const exportPlan = useCallback(async (): Promise<void> => {
    if (!floorPlan) {
      return;
    }

    const exported = await exportFloorPlan(floorPlan.floorPlanId, keycloak);

    if (!exported) {
      notify(t('floorplan-editor-export-failed'));
    }
  }, [floorPlan, keycloak, t]);

  return {
    saving, issues, save, saveDraft, schedule, exportPlan,
  };
};

export default useSaveFloorPlan;
